import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { Vault } from '@/services/contract/contract.types.ts'
import {
  useVaultMetadata,
  type VaultMetadata,
} from '@/hooks/useVaultMetadata.ts'
import { useVaults } from '@/hooks/useVaults.ts'
import { useLocalCountdown } from '@/hooks/useLocalCountdown.ts'
import { useNotifications } from '@/hooks/useNotifications.ts'
import { useProfile } from '@/hooks/useProfile.ts'
import { useActivityLog } from '@/hooks/useActivityLog.ts'
import {
  sendCountdownStartedEmailsWebplatform,
  sendNotifyBeneficiaryVaultEmailWebplatform,
} from '@/services/notification/webplatformEmailSenders.ts'
import { getFriendlyTransactionError } from '@/utils/walletError.ts'
import { cn } from '@/utils/cn.ts'

interface VaultCardProps {
  vault: Vault
  role: 'benefactor' | 'beneficiary'
  onVaultUpdated?: () => void
}

function shortenAddress(addr: string): string {
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`
}

function formatTimeRemaining(seconds: bigint): string {
  const s = Number(seconds)
  if (s <= 0) return '0m'
  const days = Math.floor(s / 86400)
  const hours = Math.floor((s % 86400) / 3600)
  const mins = Math.floor((s % 3600) / 60)
  const secs = Math.floor(s % 60)
  if (days > 0) return `${days}d ${hours}h ${mins}m`
  if (hours > 0) return `${hours}h ${mins}m`
  if (mins > 0) return `${mins}m ${secs}s`
  return `${secs}s`
}

export function VaultCard({ vault, role, onVaultUpdated }: VaultCardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { fetchByVaultId } = useVaultMetadata()
  const { startCountdown, getTimeRemaining, fetchVaultDetails } = useVaults()
  const { timeRemaining } = useLocalCountdown(
    vault.countdownStartTime,
    vault.countdownDuration,
    vault.isCountdownActive,
    () => getTimeRemaining(vault.id),
  )
  const { sendSMS } = useNotifications()
  const { getProfileByWallet } = useProfile()
  const { logActivity } = useActivityLog()

  const [meta, setMeta] = useState<VaultMetadata | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [actionFeedback, setActionFeedback] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)
  const [resendLoading, setResendLoading] = useState<'email' | 'sms' | null>(
    null,
  )

  useEffect(() => {
    fetchByVaultId(Number(vault.id)).then(setMeta)
  }, [vault.id, fetchByVaultId])

  const isCountdownExpired =
    vault.isCountdownActive && timeRemaining !== null && timeRemaining <= 0n

  const statusLabel = isCountdownExpired
    ? t('vaultCard.countdownExpired')
    : vault.isCountdownActive
      ? t('vaultCard.countdownActive')
      : t('vaultCard.countdownNotStarted')

  const statusDotClass = isCountdownExpired
    ? 'bg-success'
    : vault.isCountdownActive
      ? 'bg-warning'
      : 'bg-fg-muted'

  const counterpartyAddress =
    role === 'benefactor' ? vault.beneficiary : vault.benefactor
  const counterpartyLabel =
    role === 'benefactor'
      ? t('vaultCard.beneficiary')
      : t('vaultCard.benefactor')

  const handleStartCountdown = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (actionLoading) return
    setActionLoading(true)
    setActionFeedback(null)
    try {
      const result = await startCountdown(vault.id)
      if (!result) {
        setActionFeedback({
          type: 'error',
          message: t('common.transactionFailed', {
            defaultValue: 'Transaction failed or was rejected.',
          }),
        })
        return
      }
      const v = await fetchVaultDetails(vault.id)
      const m = await fetchByVaultId(Number(vault.id))
      if (v) {
        const bfProf = await getProfileByWallet(v.benefactor)
        const benProf = await getProfileByWallet(v.beneficiary)
        await sendCountdownStartedEmailsWebplatform({
          vaultId: Number(vault.id),
          countdownDurationSeconds: Number(v.countdownDuration),
          benefactorAddress: v.benefactor,
          beneficiaryAddress: v.beneficiary,
          benefactorEmail: bfProf?.email ?? null,
          beneficiaryEmail: m?.beneficiary_email ?? null,
          benefactorName: bfProf?.name ?? null,
          notifyBenefactorEmail: bfProf?.notify_email,
          notifyBeneficiaryEmail: benProf?.notify_email,
        })
      }
      onVaultUpdated?.()
      setActionFeedback({
        type: 'success',
        message: t('activity.action_countdown_started'),
      })
    } catch (err) {
      setActionFeedback({
        type: 'error',
        message: getFriendlyTransactionError(err),
      })
    } finally {
      setActionLoading(false)
    }
  }

  const canDecrypt =
    role === 'beneficiary' &&
    vault.isCountdownActive &&
    timeRemaining !== null &&
    timeRemaining <= 0n
  const showStartCountdown = role === 'beneficiary' && !vault.isCountdownActive

  const detailUrl = `/dashboard/vault/${vault.id.toString()}`

  const handleResendEmail = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!meta?.beneficiary_email || resendLoading) return
    const profile = await getProfileByWallet(vault.beneficiary)
    if (profile?.notify_email === false) return
    setResendLoading('email')
    try {
      await sendNotifyBeneficiaryVaultEmailWebplatform({
        vaultId: Number(vault.id),
        beneficiaryEmail: meta.beneficiary_email,
        beneficiaryAddress: vault.beneficiary,
      })
      await logActivity('notification_sent', Number(vault.id), {
        channel: 'email',
        resend: true,
      })
    } finally {
      setResendLoading(null)
    }
  }

  const handleResendSms = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!meta?.beneficiary_phone || resendLoading) return
    const profile = await getProfileByWallet(vault.beneficiary)
    if (profile?.notify_sms === false) return
    setResendLoading('sms')
    try {
      await sendSMS({
        to: meta.beneficiary_phone,
        message: t('vaultCard.resendSmsMessage', { id: vault.id.toString() }),
      })
      await logActivity('notification_sent', Number(vault.id), {
        channel: 'sms',
        resend: true,
      })
    } finally {
      setResendLoading(null)
    }
  }

  return (
    <div className="border-line bg-surface-card duration-fast hover:border-accent hover:bg-surface-card-hover hover:shadow-glow flex flex-col gap-4 rounded-xl border p-6 transition-all">
      <Link
        to={detailUrl}
        className="flex flex-1 flex-col gap-4 text-inherit no-underline"
      >
        <div className="flex items-center justify-between">
          <span className="bg-accent-muted text-accent rounded-sm px-2 py-1 font-mono text-[0.8125rem]">
            #{vault.id.toString()}
            {meta?.vault_name ? ` - ${meta.vault_name}` : ''}
          </span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-semibold tracking-wide uppercase',
              meta?.vault_type === 'file'
                ? 'bg-teal-muted text-teal'
                : 'bg-info-muted text-info',
            )}
          >
            {meta?.vault_type ?? t('vaultCard.vault')}
          </span>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-fg-muted text-xs tracking-wider uppercase">
            {counterpartyLabel}
          </span>
          <span className="text-fg-secondary truncate font-mono text-[0.8125rem]">
            {shortenAddress(counterpartyAddress)}
          </span>
        </div>

        <div className="border-line-subtle flex items-center gap-2 border-t pt-2">
          <span
            className={cn('h-2 w-2 shrink-0 rounded-full', statusDotClass)}
          />
          <span className="text-fg-secondary text-[0.8125rem]">
            {statusLabel}
          </span>
        </div>
      </Link>

      {role === 'benefactor' &&
        (meta?.beneficiary_email || meta?.beneficiary_phone) && (
          <div
            className="border-line-subtle flex items-center gap-2 border-t pt-2"
            onClick={(e) => e.preventDefault()}
            onKeyDown={(e) => e.preventDefault()}
            role="group"
            aria-label={t('vaultCard.resendNotification')}
          >
            {meta.beneficiary_email && (
              <button
                type="button"
                className="border-line bg-surface-muted text-fg duration-fast hover:border-accent cursor-pointer rounded-sm border px-2 py-1.5 text-[0.9375rem] transition-all disabled:cursor-not-allowed disabled:opacity-60"
                disabled={resendLoading !== null}
                onClick={handleResendEmail}
                title={t('vaultDetail.resendEmail')}
              >
                {resendLoading === 'email' ? '...' : '✉'}
              </button>
            )}
            {meta.beneficiary_phone && (
              <button
                type="button"
                className="border-line bg-surface-muted text-fg duration-fast hover:border-accent cursor-pointer rounded-sm border px-2 py-1.5 text-[0.9375rem] transition-all disabled:cursor-not-allowed disabled:opacity-60"
                disabled={resendLoading !== null}
                onClick={handleResendSms}
                title={t('vaultDetail.resendSms')}
              >
                {resendLoading === 'sms' ? '...' : '📱'}
              </button>
            )}
          </div>
        )}

      {role === 'beneficiary' && (
        <div
          className="border-line-subtle flex flex-wrap items-center gap-2 border-t pt-2"
          onClick={(e) => e.preventDefault()}
        >
          {showStartCountdown && (
            <>
              <button
                type="button"
                className="border-line bg-surface-muted text-fg duration-fast hover:border-accent cursor-pointer rounded-md border px-4 py-2 text-[0.8125rem] font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleStartCountdown}
                disabled={actionLoading}
              >
                {actionLoading
                  ? t('common.pending', { defaultValue: 'Pending...' })
                  : t('vaultCard.startCountdown')}
              </button>
              {actionFeedback && (
                <span
                  className={cn(
                    'text-[0.8125rem] font-medium',
                    actionFeedback.type === 'success'
                      ? 'text-success'
                      : 'text-error',
                  )}
                  role="status"
                >
                  {actionFeedback.message}
                </span>
              )}
            </>
          )}
          {vault.isCountdownActive &&
            timeRemaining !== null &&
            timeRemaining > 0n && (
              <span className="text-fg-secondary text-[0.8125rem]">
                {t('vaultCard.countdownInProgress')}:{' '}
                {formatTimeRemaining(timeRemaining)}
              </span>
            )}
          {canDecrypt && (
            <button
              type="button"
              className="bg-gradient-brand text-fg-inverse duration-fast cursor-pointer rounded-md border-none px-4 py-2 text-[0.8125rem] font-semibold transition-opacity hover:opacity-90"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                navigate(detailUrl)
              }}
            >
              {t('vaultCard.decrypt')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
